import { SWRFetcher } from '@commenda/utils';
import useSWR, { type SWRConfiguration } from 'swr';

import {
  TIndirectTaxTransactionAggregates,
  TIndirectTaxListTransactionsParams,
} from './types';
import { IndirectTaxTransactionQueryUtils } from './transaction-query-utils';

// Aggregates fan out over the same filters as the list endpoint, so refresh
// less often and avoid focus-triggered bursts against the database.
const TRANSACTION_AGGREGATE_SWR_OPTIONS: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
  refreshWhenHidden: false,
  dedupingInterval: 60_000,
  refreshInterval: 5 * 60_000,
};

export function useIndirectTaxTransactionAggregates({
  token,
  filters,
  enabled = true,
}: Omit<TIndirectTaxListTransactionsParams, 'sortState'> & {
  enabled?: boolean;
}) {
  const query = IndirectTaxTransactionQueryUtils.buildQueryParams(filters);

  const { data, error, isLoading, mutate } =
    useSWR<TIndirectTaxTransactionAggregates>(
      enabled ? `new-sales-tax/transactions/aggregate?${query}` : null,
      SWRFetcher({ method: 'GET' }, token),
      TRANSACTION_AGGREGATE_SWR_OPTIONS
    );

  // Default values for first paint while SWR is loading. Currency, dateGroups
  // and FX counters omitted intentionally — they're only meaningful once the
  // server responds.
  const defaultAggregates: TIndirectTaxTransactionAggregates = {
    totalTransactions: 0,
    totalSales: 0,
    totalTaxCollected: 0,
    totalPurchases: 0,
    totalTaxPaid: 0,
  };

  return {
    aggregates: data || defaultAggregates,
    error,
    mutate,
    isLoading,
  };
}
